import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input } from '@besiktning/ui'
import { useAuthStore } from '../store/authStore'
import { Mail } from 'lucide-react'
import { Logo } from '../components/Logo'
import { apiUrl } from '../lib/apiBase'
import { t } from '../i18n'

export default function Login() {
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  
  const navigate = useNavigate()
  const { login, isAuthenticated, requiresMembershipSelection, isInitializing } = useAuthStore()

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated && !isInitializing) {
      if (requiresMembershipSelection) {
        navigate('/select-account', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    }
  }, [isAuthenticated, requiresMembershipSelection, isInitializing, navigate])

  // Show loading while checking auth status
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    )
  }

  // Simple email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    // Validate email
    if (!isValidEmail(email)) {
      setError(t('login.errInvalidEmail'))
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch(apiUrl('/auth/request_otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      })

      if (response.ok) {
        setStep('otp')
      } else {
        const data = await response.json()
        setError(data.error || t('login.errSendOtp'))
      }
    } catch {
      setError(t('login.errGeneric'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const response = await fetch(apiUrl('/auth/verify_otp'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: otp }),
      })

      const data = await response.json()

      if (response.ok) {
        // Pass membership data to login
        login(
          data.user, 
          data.token, 
          data.memberships || [], 
          data.current_membership,
          data.requires_membership_selection
        )
        
        // Navigate based on whether membership selection is required
        if (data.requires_membership_selection) {
          navigate('/select-account')
        } else {
          navigate('/')
        }
      } else if (response.status === 403) {
        setError(data.error || t('login.errNoAccess'))
      } else {
        setError(data.error || t('login.errWrongCode'))
      }
    } catch {
      setError(t('login.errGeneric'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <Logo className="h-20 sm:h-24" />
          </div>
          <p className="text-gray-600">{t('login.tagline')}</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          {step === 'email' ? (
            <form onSubmit={handleRequestOTP}>
              <h2 className="text-lg font-semibold mb-4 flex items-center">
                <Mail className="mr-2 h-5 w-5" />
                {t('login.emailHeading')}
              </h2>

              <Input
                label={t('login.emailLabel')}
                type="email"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mb-4"
              />

              {error && (
                <p className="text-sm text-red-600 mb-4">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full"
                isLoading={isLoading}
                disabled={!email.trim()}
              >
                {t('login.sendCode')}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <h2 className="text-lg font-semibold mb-4">{t('login.otpHeading')}</h2>

              <p className="text-sm text-gray-600 mb-4">
                {t('login.otpSentTo')} <strong>{email}</strong>
              </p>

              <Input
                label={t('login.otpLabel')}
                type="text"
                placeholder="123456"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                maxLength={6}
                required
                className="mb-4"
              />

              {error && (
                <p className="text-sm text-red-600 mb-4">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full"
                isLoading={isLoading}
              >
                {t('login.login')}
              </Button>

              <button
                type="button"
                onClick={() => setStep('email')}
                className="w-full mt-3 text-sm text-blue-600 hover:text-blue-700"
              >
                {t('login.changeEmail')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
